import React, { useState } from 'react'
import { message } from 'antd'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { CensusFileRowDto } from '../api/dto/census-file-row.dto'
import {
  bulkActionCensusFileRows,
  updateCensusFileRows,
} from '../api/census-enrollment-platform-api-client'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { mapGridDataToCensusFileRowDto } from '../utils/map-grid-data-to-census-file-row-dto'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { PatchActionEnum } from '../api/enums/patch-action.enum'
import { CensusFileRowCountsDto } from '../api/dto/census-file-row-counts.dto'
import WithdrawOnlySelectedButton from './WithdrawButton/WithdrawOnlySelectedButton'
import { WithdrawAllButton } from './WithdrawButton/WithdrawAllButton'
import WithdrawAllConfirmationModal from './WithdrawButton/WithdrawAllConfirmationModal'

interface WithdrawSelectedButtonProps {
  selectedRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  cleanAllRows?: () => void
  setLoading: (loading: boolean) => void // Prop to update loading state in parent
  buttonText?: string
  type?: CensusFileRowTypeEnum
  publicKey: string
  allRows: CensusFileRowGridData[]
  counts?: CensusFileRowCountsDto
}

// Set state of rows to Withdrawn
const withdrawRows = async (
  rowsToWithdraw: CensusFileRowGridData[],
  publicKey: string
) => {
  const withdrawnRows: CensusFileRowDto[] = rowsToWithdraw.map((gridRow) => ({
    ...mapGridDataToCensusFileRowDto(gridRow),
    status: CensusFileRowStatusEnum.WITHDRAWN,
  }))
  await updateCensusFileRows(
    withdrawnRows,
    publicKey,
    PatchActionEnum.WITHDRAW_RECORDS
  )
}

const WithdrawSelectedButton = ({
  selectedRows,
  handleSubmit,
  cleanAllRows,
  setLoading,
  buttonText,
  publicKey,
  allRows,
  counts,
}: WithdrawSelectedButtonProps) => {
  const [loading, setButtonLoading] = useState(false)
  const [showWithdrawAllModal, setShowWithdrawAllModal] = useState(false)

  const handleClick = async (rows: CensusFileRowGridData[]) => {
    if (rows.length === 0) {
      message.error('No rows selected')
      return
    }

    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false
    let succeeded = false

    // Start a delayed loading indicator (only if the request takes longer than 1000ms)
    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      await withdrawRows(rows, publicKey)
      succeeded = true
      message.success(`${rows.length} records withdrawn`, 5)
    } catch (error) {
      const msg = `Failed to withdraw records: '${(error as Error).message}'`
      console.error(msg)
      message.error(msg)
    } finally {
      // Clear loading state if it was set
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) {
        clearTimeout(timer)
      }
      if (succeeded) {
        handleSubmit(rows)
        cleanAllRows?.()
      }
    }
  }

  const handleWithdrawAll = () => {
    setShowWithdrawAllModal(true)
  }

  const executeWithdrawAll = async () => {
    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false
    let succeeded = false

    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      await bulkActionCensusFileRows(
        publicKey,
        PatchActionEnum.WITHDRAW_RECORDS
      )
      succeeded = true
      message.success('All records withdrawn successfully', 5)
      setShowWithdrawAllModal(false)
    } catch (error) {
      message.error(`Failed to withdraw records: '${(error as Error).message}'`)
    } finally {
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) {
        clearTimeout(timer)
      }
      if (succeeded) {
        handleSubmit(allRows)
        cleanAllRows?.()
      }
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: '8px' }}>
        <WithdrawAllButton
          handleWithdrawAll={handleWithdrawAll}
          loading={loading}
        />
        {selectedRows.length > 0 ? (
          <WithdrawOnlySelectedButton
            selectedRows={selectedRows}
            loading={loading}
            buttonText={buttonText}
            handleClick={() => handleClick(selectedRows)}
          />
        ) : null}
      </div>
      <WithdrawAllConfirmationModal
        visible={showWithdrawAllModal}
        onCancel={() => setShowWithdrawAllModal(false)}
        onConfirm={executeWithdrawAll}
        loading={loading}
        recordCount={allRows.length}
        counts={counts}
      />
    </>
  )
}

export default WithdrawSelectedButton
